import React, { useContext as useContextCore, useMemo } from 'react'
import type { UseDocumentResult, UseListResult, UseListWithSelection, UseListProps } from '@ztechapps/common.hook'
import { useListWithSelection } from '@ztechapps/common.hook'
import type { Order, RouteDef } from '@ztechapps/common.contract'
import { useGetNetTotal, useOrderList } from '@ztechapps/order.common'

export type OrderListContextProps = {
  filterCategory?: string
  children: React.ReactNode
  orderItemRoute: RouteDef
  customerItemRoute: RouteDef
  companyItemRoute: RouteDef
  zendeskRootURL: string
  listProps?: Partial<UseListProps>
}
export type OrderListProps = Omit<OrderListContextProps, 'children'>

export type OrderListContextType = {
  filterCategory: string
  selectedOrderIds: string[]
  selectedOrders: Order[]
  orderList: UseListResult<Order>
  orderListWithSelection: UseListWithSelection<Order>
  getNetTotalResult: UseDocumentResult
  onRefresh: () => Promise<void>
} & OrderListProps

const context = React.createContext({} as OrderListContextType)
const CoreProvider = context.Provider
const defaultFilterCategory = 'orders-FilterCategory'

export const OrderListProvider = ({ children, filterCategory, listProps, ...rest }: OrderListContextProps) => {
  //set the default filterCategory
  filterCategory = filterCategory ?? defaultFilterCategory

  const orderList = useOrderList({ listActionOptions: { storageKey: filterCategory }, ...listProps })
  const orderListWithSelection = useListWithSelection(orderList?.list)
  const selectedOrderIds = useMemo(() => {
    return orderListWithSelection.selectedList.map((item) => item._id)
  }, [orderListWithSelection])
  const selectedOrders = orderListWithSelection.selectedList
  const getNetTotalResult = useGetNetTotal(orderList.filter)

  const onRefresh = async () => {
    await orderList.refetch()
    await getNetTotalResult.refetch()
  }

  return (
    <CoreProvider
      value={{
        ...rest,
        orderList,
        orderListWithSelection,
        filterCategory,
        selectedOrders,
        selectedOrderIds,
        getNetTotalResult,
        onRefresh,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useOrderListContext = () => useContextCore(context)
